/**
 * Onboarding and profile editing (docs/GAME_DESIGN.md § 8), four short steps:
 * nickname → grade → school (country → province → city → school) →
 * leaderboard opt-in. Only needed for leaderboards; players can skip it and
 * play anyway. No age is ever asked. The form model is screens/model/onboarding.ts.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { Locale } from '@dropstack/core';
import type { BackendClient, PlayerProfile } from '../contracts.ts';
import { useT } from '../i18n/index.ts';
import { BackendError } from '../services/backend/errors.ts';
import { suggestNicknames, NICKNAME_MAX_LENGTH } from '../services/nicknames.ts';
import { randomSeed } from '../services/random.ts';
import { Button } from '../components/Button.tsx';
import { ChoiceGroup } from '../components/Controls.tsx';
import { Icon } from '../components/Icon.tsx';
import { ScreenLayout } from '../components/ScreenLayout.tsx';
import type { OnboardingMode } from './model/router.ts';
import {
  GRADES,
  ONBOARDING_STEPS,
  draftFromProfile,
  draftToInput,
  fieldProblemKey,
  firstInvalidStep,
  nextStep,
  previousStep,
  stepForField,
  stepIndex,
  stepProblem,
  type OnboardingStepId,
  type ProfileDraft,
} from './model/onboarding.ts';
import { SchoolStep } from './onboarding/SchoolStep.tsx';
import './onboarding.css';

export interface OnboardingScreenProps {
  mode: OnboardingMode;
  /** The step to open on (e.g. straight to the opt-in for an onboarded player). */
  step: OnboardingStepId;
  backend: BackendClient | null;
  profile: PlayerProfile | null;
  locale: Locale;
  onSaved(profile: PlayerProfile): void;
  /** Leave without saving. */
  onSkip(): void;
}

const STEP_TITLE_KEYS: Record<OnboardingStepId, string> = {
  name: 'shell.obNameTitle',
  grade: 'shell.obGradeTitle',
  school: 'shell.obSchoolTitle',
  leaderboard: 'shell.obBoardTitle',
};

export function OnboardingScreen({ mode, step: initialStep, backend, profile, locale, onSaved, onSkip }: OnboardingScreenProps) {
  const t = useT();
  const [step, setStep] = useState<OnboardingStepId>(initialStep);
  const [draft, setDraft] = useState<ProfileDraft>(() => draftFromProfile(profile));
  /** A problem is only shown once the player tried to move on (no red text while typing the first letter). */
  const [shownProblem, setShownProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const firstRender = useRef(true);

  const update = (patch: Partial<ProfileDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setShownProblem(null);
  };

  // Move focus to the new step's heading so screen readers follow along.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    stepHeadingRef.current?.focus();
  }, [step]);

  const goTo = (next: OnboardingStepId) => {
    setShownProblem(null);
    setStep(next);
  };

  const save = async () => {
    const input = draftToInput(draft, locale);
    if (!input) {
      const bad = firstInvalidStep(draft) ?? 'name';
      setStep(bad);
      setShownProblem(stepProblem(bad, draft) ?? 'shell.obSaveFailed');
      return;
    }
    if (!backend) {
      setShownProblem('shell.obSaveOffline');
      return;
    }
    setSaving(true);
    try {
      onSaved(await backend.saveProfile(input));
    } catch (err) {
      const first = err instanceof BackendError ? err.details[0] : undefined;
      if (first) {
        setStep(stepForField(first.field));
        setShownProblem(fieldProblemKey(first));
      } else {
        setShownProblem(err instanceof BackendError && err.isUnreachable ? 'shell.obSaveOffline' : 'shell.obSaveFailed');
      }
    } finally {
      setSaving(false);
    }
  };

  const next = () => {
    const problem = stepProblem(step, draft);
    if (problem) {
      setShownProblem(problem);
      return;
    }
    const following = nextStep(step);
    if (following) goTo(following);
    else void save();
  };

  const back = previousStep(step);
  const index = stepIndex(step);
  const isLast = index === ONBOARDING_STEPS.length - 1;
  const title = mode === 'edit' ? t('shell.obEditTitle') : t('shell.obJoinTitle');

  return (
    <ScreenLayout title={title} onBack={onSkip} backLabel={t('common.back')} className="onboarding">
      <form
        className="ui-panel onboarding__card"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          next();
        }}
      >
        <ol className="onboarding__dots" aria-label={t('shell.obProgress', { step: index + 1, total: ONBOARDING_STEPS.length })}>
          {ONBOARDING_STEPS.map((s, i) => (
            <li key={s} className={i < index ? 'is-done' : i === index ? 'is-current' : undefined} aria-hidden="true" />
          ))}
        </ol>
        <p className="onboarding__step-count">{t('shell.obProgress', { step: index + 1, total: ONBOARDING_STEPS.length })}</p>
        <h2 ref={stepHeadingRef} tabIndex={-1} className="onboarding__title">
          {t(STEP_TITLE_KEYS[step])}
        </h2>

        {step === 'name' && <NameStep draft={draft} locale={locale} update={update} problem={shownProblem} />}
        {step === 'grade' && <GradeStep draft={draft} update={update} />}
        {step === 'school' && <SchoolStep draft={draft} update={update} backend={backend} locale={locale} />}
        {step === 'leaderboard' && <BoardStep draft={draft} update={update} />}

        <p className="onboarding__problem" role="alert">
          {shownProblem && step !== 'name' ? t(shownProblem) : ''}
        </p>

        <div className="onboarding__nav">
          {back ? (
            <Button icon="back" onClick={() => goTo(back)} disabled={saving}>
              {t('common.back')}
            </Button>
          ) : (
            <span />
          )}
          <Button type="submit" variant="primary" iconEnd={isLast ? 'check' : 'chevron'} busy={saving}>
            {isLast ? (mode === 'edit' ? t('common.save') : t('shell.obFinish')) : t('common.next')}
          </Button>
        </div>
      </form>

      {/* Joining is optional: the way out is always in view, never hidden in a corner. */}
      {mode === 'join' && (
        <Button variant="ghost" className="onboarding__skip" disabled={saving} onClick={onSkip}>
          {t('shell.obSkip')}
        </Button>
      )}
    </ScreenLayout>
  );
}

interface StepProps {
  draft: ProfileDraft;
  update(patch: Partial<ProfileDraft>): void;
}

function NameStep({ draft, update, locale, problem }: StepProps & { locale: Locale; problem: string | null }) {
  const t = useT();
  const inputId = useId();
  const [seed, setSeed] = useState(randomSeed);
  const suggestions = suggestNicknames(locale, seed, 4);

  return (
    <div className="onboarding__step">
      <p className="onboarding__text">{t('shell.obNameText')}</p>
      <p className="onboarding__warning">
        <Icon name="info" />
        <span>{t('shell.obNameWarning')}</span>
      </p>
      <label className="onboarding__label" htmlFor={inputId}>
        {t('shell.obNameLabel')}
      </label>
      <input
        id={inputId}
        className="ui-input"
        value={draft.displayName}
        maxLength={NICKNAME_MAX_LENGTH + 5}
        autoComplete="off"
        autoCapitalize="words"
        spellCheck={false}
        aria-invalid={problem ? true : undefined}
        aria-describedby={problem ? `${inputId}-problem` : undefined}
        onChange={(e) => update({ displayName: e.currentTarget.value })}
      />
      <p id={`${inputId}-problem`} className="onboarding__problem" role="alert">
        {problem ? t(problem) : ''}
      </p>
      <div className="onboarding__suggestions">
        <p className="onboarding__label">{t('shell.obNameIdeas')}</p>
        <ul className="onboarding__chips">
          {suggestions.map((name) => (
            <li key={name}>
              <button
                type="button"
                className="ui-chip"
                aria-pressed={draft.displayName === name}
                onClick={() => update({ displayName: name })}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
        <Button variant="ghost" icon="shuffle" onClick={() => setSeed((s) => (s + 1) >>> 0)}>
          {t('shell.obNameMore')}
        </Button>
      </div>
    </div>
  );
}

function GradeStep({ draft, update }: StepProps) {
  const t = useT();
  return (
    <div className="onboarding__step">
      <p className="onboarding__text">{t('shell.obGradeText')}</p>
      <ChoiceGroup<number>
        legend={t('shell.obGradeTitle')}
        hideLegend
        variant="grid"
        value={draft.grade}
        onChange={(grade) => update({ grade })}
        choices={GRADES.map((g) => ({
          value: g,
          label: (
            <>
              <span className="onboarding__grade-word">{t('shell.obGradeWord')}</span>
              <span className="onboarding__grade-number">{g}</span>
            </>
          ),
        }))}
      />
    </div>
  );
}

function BoardStep({ draft, update }: StepProps) {
  const t = useT();
  return (
    <div className="onboarding__step">
      <p className="onboarding__text">{t('shell.obBoardText')}</p>
      <ul className="onboarding__public">
        <li>{t('shell.setPrivacyItemName')}</li>
        <li>{t('shell.setPrivacyItemSchool')}</li>
        <li>{t('shell.setPrivacyItemScores')}</li>
      </ul>
      <p className="onboarding__text">{t('shell.setPrivacyNever')}</p>
      <p className="onboarding__warning onboarding__warning--parent">
        <Icon name="user" />
        <span>{t('shell.obBoardParent')}</span>
      </p>
      <ChoiceGroup<'yes' | 'no'>
        legend={t('shell.obBoardTitle')}
        hideLegend
        variant="cards"
        value={draft.leaderboardOptIn ? 'yes' : 'no'}
        onChange={(v) => update({ leaderboardOptIn: v === 'yes' })}
        choices={[
          { value: 'yes', label: t('shell.obBoardYes'), description: t('shell.obBoardYesDesc') },
          { value: 'no', label: t('shell.obBoardNo'), description: t('shell.obBoardNoDesc') },
        ]}
      />
    </div>
  );
}
